// ---------------------------------------------------------------------------------------------------------------------
// ABOUT MEDIA WITH HEADING
//
// Reveals ONE shared source (the section's video or image) through the section's "window" blocks. The windows are laid
// out by flex, so their positions depend on the live layout (text lengths, widths, wrapping, screen size) and cannot be
// known in CSS. This element measures every window and clips the single source to the union of their rectangles, so the
// source is painted only where the windows are — like one screen seen through several holes in a wall. One element means
// the windows are always in perfect sync.
//
// Progressive enhancement: the clip is applied by JS only. With no JS the source simply shows behind the text (a plain
// video hero) — never a blank section. So JS is never the only thing that makes content visible.
//
// Registered the theme's way: a guarded custom element with connectedCallback (re-runs on every theme-editor re-render)
// and AbortController cleanup in disconnectedCallback.
// ---------------------------------------------------------------------------------------------------------------------

if (!window.customElements.get('about-media-weave')) {
  window.customElements.define(
    'about-media-weave',
    class AboutMediaWeave extends HTMLElement {
      connectedCallback() {
        this.source = this.querySelector('[data-amwh-source]');
        if (!this.source) return;

        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.update = this.update.bind(this);
        this.schedule = this.schedule.bind(this);

        // Any layout change (viewport, wrapping, a window resizing, fonts loading) should recompute the clip.
        this.resizeObserver = new ResizeObserver(this.schedule);
        this.resizeObserver.observe(this);
        this.querySelectorAll('[data-amwh-window], [data-amwh-word]').forEach((el) => this.resizeObserver.observe(el));

        window.addEventListener('resize', this.schedule, { signal });
        window.addEventListener('orientationchange', this.schedule, { signal });

        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(this.schedule);
        }

        // Recompute while the merchant edits the section/blocks in the theme editor.
        document.addEventListener('shopify:section:load', this.schedule, { signal });
        document.addEventListener('shopify:section:select', this.schedule, { signal });
        document.addEventListener('shopify:block:select', this.schedule, { signal });

        this.setAttribute('data-ready', '');
        this.schedule();
      }

      disconnectedCallback() {
        if (this.abortController) this.abortController.abort();
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.raf) cancelAnimationFrame(this.raf);
      }

      // Coalesce bursts of events into a single measurement per frame.
      schedule() {
        if (this.raf) cancelAnimationFrame(this.raf);
        this.raf = requestAnimationFrame(this.update);
      }

      update() {
        this.raf = null;
        if (!this.isConnected || !this.source) return;

        // Mobile (<=767px) stacks the heading over the full video (CSS) — remove any clip so the whole video shows.
        if (window.matchMedia('(max-width: 767px)').matches) {
          this.source.style.clipPath = '';
          return;
        }

        const windows = this.querySelectorAll('[data-amwh-window]');
        if (!windows.length) {
          // No windows to peek through — hide the source entirely.
          this.source.style.clipPath = 'inset(100%)';
          return;
        }

        const radius = parseFloat(getComputedStyle(this).getPropertyValue('--amwh-window-radius')) || 0;

        // Coordinates are relative to the source's own box, which is what clip-path: path() resolves against.
        const base = this.source.getBoundingClientRect();
        let d = '';

        windows.forEach((win) => {
          const r = win.getBoundingClientRect();
          if (r.width <= 0 || r.height <= 0) return;
          d += this.constructor.rectPath(r.left - base.left, r.top - base.top, r.width, r.height, radius);
        });

        this.source.style.clipPath = d ? `path('${d}')` : 'inset(100%)';
      }

      // One (optionally rounded) rectangle as an SVG path sub-path. Multiple sub-paths = multiple disjoint holes.
      static rectPath(x, y, w, h, radius) {
        const r = Math.max(0, Math.min(radius, w / 2, h / 2));
        const x2 = x + w;
        const y2 = y + h;
        const f = (n) => n.toFixed(2);

        if (r === 0) {
          return `M${f(x)} ${f(y)}L${f(x2)} ${f(y)}L${f(x2)} ${f(y2)}L${f(x)} ${f(y2)}Z`;
        }

        return (
          `M${f(x + r)} ${f(y)}` +
          `L${f(x2 - r)} ${f(y)}A${f(r)} ${f(r)} 0 0 1 ${f(x2)} ${f(y + r)}` +
          `L${f(x2)} ${f(y2 - r)}A${f(r)} ${f(r)} 0 0 1 ${f(x2 - r)} ${f(y2)}` +
          `L${f(x + r)} ${f(y2)}A${f(r)} ${f(r)} 0 0 1 ${f(x)} ${f(y2 - r)}` +
          `L${f(x)} ${f(y + r)}A${f(r)} ${f(r)} 0 0 1 ${f(x + r)} ${f(y)}Z`
        );
      }
    }
  );
}
